import {faArrowLeftLong} from "@fortawesome/free-solid-svg-icons"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import React, {Dispatch, SetStateAction, useCallback, useContext, useRef} from "react"
import ReactMarkdown from "react-markdown"
import {useNavigate, useParams} from "react-router-dom"
import * as Diff from "diff"
import remarkGfm from "remark-gfm"
import {ProfileContext} from "../../../../context/Profile.context"
import {notifyUpdate} from "../../../../functions/ipfs-orbit"
import {getTimeFromNow} from "../../../../functions/time"
import {useAPI} from "../../../../hooks/useAPI"
import {useGitDiff} from "../../../../hooks/useGitDiff"
import {RepoDB} from "../../../../models/RepoDB.model"
import styles from "./index.module.scss"
import {BackendContext} from "../../../../context/BackendContext"
import {UIHelperContext} from "../../../../context/UIHelper.context"
import {useGitDir} from "../../../../hooks/useGitDir"
import CodeDiffViewer from "../../../../components/CodeDiffViewer"
import {v4 as uuidv4} from "uuid"

interface ShowPRProps {
  repoDB?: RepoDB
  repoReplCnt: number
  setRepoReplCnt: Dispatch<SetStateAction<number>>
}

export const ShowPR = (props: ShowPRProps) => {
  const {repoDB, setRepoReplCnt} = props
  const {pullRequestId, orbitId, repoId} = useParams()
  const navigate = useNavigate()
  const {setSnackMessage} = useContext(UIHelperContext)
  const {profile} = useContext(ProfileContext)
  const gitDir = useGitDir(orbitId, repoId)
  const {ipfs, fs} = useContext(BackendContext)
  const newCommentTextArea = useRef<HTMLTextAreaElement>(null)
  const {healthCheck, mergeBranches} = useAPI()

  const pullRequest = repoDB?.get(`pullRequest_${pullRequestId}`)
  const baseBranch = pullRequest?.baseBranch
  const mergeBranch = pullRequest?.mergeBranch
  const {commits, diffString} = useGitDiff(fs, gitDir, mergeBranch, baseBranch)

  const getDiffStats = useCallback((diffString: string) => {
    const lines = Diff.parsePatch(diffString!).flatMap((diff) => diff.hunks.flatMap((h) => h.lines))
    return {
      added: lines.filter((l) => l.startsWith("+")).length,
      removed: lines.filter((l) => l.startsWith("-")).length
    }
  }, [])

  const onSubmitNewComment = useCallback(async () => {
    const pullRequest = repoDB?.get(`pullRequest_${pullRequestId}`)
    if (!ipfs || !repoDB || !pullRequest || !profile) {
      return
    }

    const comment = newCommentTextArea.current?.value.trim()
    if (!comment) {
      setSnackMessage("Comment cannot be empty")
      return
    }

    const commentId = uuidv4()
    pullRequest[`comment_${commentId}`] = {
      id: commentId,
      authorName: profile.get("name"),
      authorProfileAddress: profile.address.toString(),
      comment,
      createAt: Date.now()
    }

    await repoDB.set(`pullRequest_${pullRequestId}`, pullRequest)
    await notifyUpdate(ipfs, repoDB.address.toString())
    setRepoReplCnt((cnt) => cnt + 1)

    newCommentTextArea.current!.value = ""
  }, [ipfs, repoDB, profile, setSnackMessage, setRepoReplCnt])

  const onMerge = useCallback(async () => {
    if (!repoDB || !pullRequest || !ipfs) {
      return
    }

    const isHealthy = await healthCheck()
    if (!isHealthy) {
      setSnackMessage("Desktop app not installed")
      return
    }

    const {success, msg, ipfsCID} = await mergeBranches(
      repoDB.address.toString(),
      pullRequest.baseBranch,
      pullRequest.mergeBranch
    )

    if (!success || ipfsCID == null) {
      setSnackMessage(msg ?? "Failed to create repo")
      return
    }

    pullRequest.status = "closed"
    await repoDB.set(`pullRequest_${pullRequest.id}`, pullRequest)
    await notifyUpdate(ipfs, repoDB.address.toString())

    setSnackMessage("Branches merged")

    navigate("../")

  }, [repoDB, navigate, pullRequest, ipfs])

  if (!profile || !repoDB || !pullRequestId || !baseBranch || !mergeBranch || !pullRequest || !diffString) {
    return null
  }

  const myName = profile.get("name")
  const comments = Object.keys(pullRequest)
    .filter((key) => key.startsWith("comment_"))
    .map((key) => pullRequest[key as `comment_${string}`])
    .sort((a, b) => a.createAt - b.createAt)

  const truncId = pullRequestId.substring(0, 8)

  return (

    <div className={styles.Container}>
      <div className={styles.Header}>
        <div>Issue #{truncId} - {pullRequest.title}</div>
        <div className={styles.Plus}>
          +{getDiffStats(diffString).added}
        </div>
        <div className={styles.Minus}>
          -{getDiffStats(diffString).removed}
        </div>
        <button className={styles.MergeButton}
                onClick={onMerge}
        >
          Merge
        </button>
      </div>

      <div className={styles.SubHeader}>
        <div>
          @{pullRequest.authorName} requested to merge {commits.length} commit(s)
        </div>
        <div className={styles.BranchMergedRow}>
          <div className={styles.BranchChip}>{baseBranch}</div>
          <FontAwesomeIcon icon={faArrowLeftLong}/>
          <div className={styles.BranchChip}>{mergeBranch}</div>
        </div>
      </div>

      <CodeDiffViewer diffString={diffString}/>

      <div className={styles.CommentsContainer}>
        <div className={styles.Header}>
          Comments ({comments.length})
        </div>

        {
          comments.map((c) => {
            return (
              <div className={styles.Comment} key={c.id}>
                <div className={styles.Desc}>
                  <div className={styles.UserName}>
                    {c.authorName}
                  </div>
                  <div className={styles.Remark}>
                    {getTimeFromNow(c.createAt, true)}
                  </div>
                </div>
                <ReactMarkdown
                  className={styles.Content}
                  remarkPlugins={[remarkGfm]}
                  children={c.comment}
                />
              </div>
            )
          })
        }

        <div className={styles.Comment}>
          <div className={styles.Desc}>
            <div className={styles.UserName}>{myName}</div>
            <div className={styles.Remark}>new comment</div>
          </div>

          <textarea placeholder={"Leave a comment"}
                    className={styles.NewComment}
                    ref={newCommentTextArea}
          />

          <button className={styles.SubmitBtn}
                  onClick={onSubmitNewComment}
          >
            Submit
          </button>
        </div>

      </div>

    </div>
  )
}
